/**
 * @file product-kind-resolve.ts
 * @description Действующий вид товара на клиенте (phase19.22, inventory.md W22).
 *
 * Сервер отдаёт `effectiveKind` только сохранённому товару, а форма должна
 * пересчитывать его на лету — при смене категории или вида, до «Сохранить».
 * Правило то же, что у `ProductKindLookupService`: свой вид товара, иначе вид
 * ближайшей категории-предка. Дерево категорий уже загружено страницей, так
 * что обход идёт в памяти.
 */

import type { CatalogCategory } from "./api-catalog";
import type { ProductKind } from "./api-product-kinds";

/** Итог резолва: вид + откуда он пришёл (для подсказки под полем). */
export interface ResolvedKind {
  kind: ProductKind | null;
  source: "product" | "category" | null;
  /** Категория, у которой задан вид, при `source = "category"`. */
  sourceCategory: CatalogCategory | null;
}

const NONE: ResolvedKind = { kind: null, source: null, sourceCategory: null };

/**
 * Вид категории по умолчанию с учётом предков. Защита от цикла — счётчик
 * шагов: битое дерево не должно вешать форму.
 */
export function resolveCategoryKind(
  categoryId: string | null | undefined,
  categoriesById: Map<string, CatalogCategory>,
  kindsById: Map<string, ProductKind>,
): ResolvedKind {
  let current = categoryId ? categoriesById.get(categoryId) : undefined;
  for (let guard = 0; current && guard < 64; guard += 1) {
    if (current.productKindId) {
      const kind = kindsById.get(current.productKindId) ?? null;
      return kind ? { kind, source: "category", sourceCategory: current } : NONE;
    }
    current = current.parentId ? categoriesById.get(current.parentId) : undefined;
  }
  return NONE;
}

/** Действующий вид товара: свой (`productKindId`) или от категории. */
export function resolveProductKind(
  input: { productKindId: string; categoryId: string },
  categoriesById: Map<string, CatalogCategory>,
  kindsById: Map<string, ProductKind>,
): ResolvedKind {
  if (input.productKindId) {
    const own = kindsById.get(input.productKindId) ?? null;
    return own ? { kind: own, source: "product", sourceCategory: null } : NONE;
  }
  return resolveCategoryKind(input.categoryId, categoriesById, kindsById);
}

/**
 * Подпись варианта «как у родителя/в категории»: «Как в категории — Пищевой»,
 * «Как в категории — не задан».
 */
export function inheritOptionLabel(prefix: string, kindName: string | null): string {
  return `${prefix} — ${kindName ?? "не задан"}`;
}

/**
 * Опции выбора вида: активные виды + текущий архивный (иначе селект покажет
 * сырой id у товара/категории, чей вид уже убрали в архив).
 */
export function kindSelectOptions(
  kinds: ProductKind[],
  currentId: string,
): Array<{ value: string; label: string }> {
  return kinds
    .filter((k) => k.isActive || k.id === currentId)
    .map((k) => ({ value: k.id, label: k.isActive ? k.name : `${k.name} (в архиве)` }));
}
